import React, { useContext, useState } from 'react';
import { Modal, Box, Button, TextField, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Project } from '../../StateContext';
import { StateContext } from '../../StateContext';
import { addProjectNoteCall, updateProjectNoteCall, deleteProjectNoteCall } from '../../Services/APICalls/apicalls';
import { useLanguage } from '../../Services/LanguageContext';
import { boxStyle } from './styles';

interface IProps {
    project: Project;
}

const ProjectNotesModal = ({ project }: IProps) => {
    const [open, setOpen] = useState(false);
    const [newContent, setNewContent] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editContent, setEditContent] = useState('');
    const { setLoginData } = useContext<any>(StateContext);
    const { t } = useLanguage();

    const notes = [...(project.notes ?? [])].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );

    const handleAdd = () => {
        if (!newContent.trim()) return;
        addProjectNoteCall(project.id, newContent.trim(), setLoginData);
        setNewContent('');
    };

    const handleStartEdit = (id: number, content: string) => {
        setEditingId(id);
        setEditContent(content);
    };

    const handleSaveEdit = (noteId: number) => {
        if (!editContent.trim()) return;
        updateProjectNoteCall(project.id, noteId, editContent.trim(), setLoginData);
        setEditingId(null);
    };

    const handleDelete = (noteId: number) => {
        deleteProjectNoteCall(project.id, noteId, setLoginData);
    };

    const formatDate = (date: Date) => {
        const d = new Date(date);
        return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
            ' ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    };

    const inputStyle = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            background: 'var(--bg-tertiary)',
            '& fieldset': { borderColor: 'var(--border-color)' },
            '&:hover fieldset': { borderColor: '#00d9ff' },
            '&.Mui-focused fieldset': { borderColor: '#00d9ff', boxShadow: '0 0 10px rgba(0, 217, 255, 0.2)' },
        },
        '& .MuiInputLabel-root': { color: 'var(--text-secondary)', '&.Mui-focused': { color: '#00d9ff' } },
        '& .MuiOutlinedInput-input': { color: 'var(--text-primary)' },
    };

    return (
        <>
            <Button variant='outlined' color='secondary' onClick={() => setOpen(true)}>
                {t('notes')}
            </Button>

            <Modal
                open={open}
                onClose={() => { setOpen(false); setEditingId(null); }}
                sx={{ '& .MuiBackdrop-root': { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.7)' } }}
            >
                <Box sx={{ ...boxStyle, maxHeight: '80vh', overflowY: 'auto', minWidth: '480px' }}>
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                        background: 'linear-gradient(90deg, #a855f7, #00d9ff)',
                        borderRadius: '16px 16px 0 0',
                    }} />

                    <h2 style={{
                        fontSize: '22px', fontWeight: 700, marginBottom: '20px',
                        background: 'linear-gradient(135deg, var(--text-primary), #a855f7)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>
                        {t('notes')} — {project.name}
                    </h2>

                    {/* Notes list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                        {notes.length === 0 && (
                            <div style={{ color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
                                {t('noNotes')}
                            </div>
                        )}
                        {notes.map(note => (
                            <div key={note.id} style={{
                                background: 'var(--bg-tertiary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '10px',
                                padding: '12px 14px',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                        {formatDate(note.created)}
                                        {note.updatedAt && (
                                            <span style={{ marginLeft: '6px', opacity: 0.6 }}>
                                                (gewijzigd {formatDate(note.updatedAt)})
                                            </span>
                                        )}
                                    </span>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        {editingId === note.id ? (
                                            <>
                                                <IconButton size='small' onClick={() => handleSaveEdit(note.id)} sx={{ color: '#22c55e' }}>
                                                    <CheckIcon fontSize='small' />
                                                </IconButton>
                                                <IconButton size='small' onClick={() => setEditingId(null)} sx={{ color: 'var(--text-secondary)' }}>
                                                    <CloseIcon fontSize='small' />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton size='small' onClick={() => handleStartEdit(note.id, note.content)} sx={{ color: '#00d9ff' }}>
                                                    <EditIcon fontSize='small' />
                                                </IconButton>
                                                <IconButton size='small' onClick={() => handleDelete(note.id)} sx={{ color: '#ef4444' }}>
                                                    <DeleteIcon fontSize='small' />
                                                </IconButton>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {editingId === note.id ? (
                                    <TextField
                                        multiline
                                        fullWidth
                                        autoFocus
                                        value={editContent}
                                        onChange={e => setEditContent(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSaveEdit(note.id); }}
                                        size='small'
                                        sx={inputStyle}
                                    />
                                ) : (
                                    <div style={{ fontSize: '14px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {note.content}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Add new note */}
                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <TextField
                            label={t('typeNoteHere')}
                            multiline
                            rows={3}
                            value={newContent}
                            onChange={e => setNewContent(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleAdd(); }}
                            sx={inputStyle}
                        />
                        <button
                            onClick={handleAdd}
                            disabled={!newContent.trim()}
                            style={{
                                width: '100%', padding: '12px 24px', fontSize: '14px', fontWeight: 600,
                                borderRadius: '10px',
                                background: newContent.trim() ? 'linear-gradient(135deg, #a855f7, #00d9ff)' : 'var(--bg-tertiary)',
                                color: newContent.trim() ? 'white' : 'var(--text-secondary)',
                                border: 'none', cursor: newContent.trim() ? 'pointer' : 'default',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {t('addNote')}
                        </button>
                    </div>
                </Box>
            </Modal>
        </>
    );
};

export default ProjectNotesModal;
